/**
 * ninegql 배럴 엔트리.
 *
 * 내부 파일 분리:
 *   - types.ts                    : 공용 타입
 *   - ext.ts                      : ResultObjBase / ResultObjBaseWithReactObj
 *   - uiAdapter.ts                : NineGQLUIAdapter / ResultObjContract
 *   - state.ts                    : 모듈 상태 + getUIAdapter/setUIAdapter/setDefaultClient
 *   - ngqlUtils.ts                : 정렬·에러·직렬화 유틸 + processSorter
 *   - ast.ts                      : GraphQL AST 빌더 + 문자열 직렬화 + getNodeData
 *   - handlers.ts                 : DetailHandler / EditFormHandler + initWithResp
 *   - uiBuilders.ts               : generateEditUI / generateDetailUI / getFormFields
 *   - PaginatedResultObjBase.ts   : ResultObj/MemTableObj 공통 base
 *   - ResultObj.ts                : ResultObj + GQLObj 팩토리 + response mapping
 *   - MemTableObj.ts              : MemTableObj + getMemTableObj 팩토리
 */

import NgqlUI from 'gql/ninegqlUI'

import type { NineGQLUIAdapter } from './uiAdapter'
import { getUIAdapter, ngqlInfo, setDefaultClient, setUIAdapter as _setUIAdapter } from './state'

import {
  Edge,
  Field,
  Fields,
  InlineFragment,
  Mutation,
  Node,
  NodeConnection,
  NodeConnection2,
  NodeExt,
  Query,
  QueryItem,
  Subscription,
  Var,
  extendField,
  generateGQL,
  generateGQLString,
  getGQLString,
  renamed,
} from './ast'
import { checkError, getOrderStrByName, getOrderStrBycCamelName } from './ngqlUtils'
import {
  generateDetailUI,
  generateDetailUIByKey,
  generateEditUI,
  getFormFields,
} from './uiBuilders'
import { initWithResp } from './handlers'
import { GQLObj, getMappingMutationRes, getMappingRes } from './ResultObj'
import { getMemTableObj } from './MemTableObj'

// 공용 타입 & 클래스 re-export
export * from './types'
export type { NineGQLUIAdapter, ResultObjContract, ReactObjLike } from './uiAdapter'
export { getUIAdapter, setDefaultClient } from './state'
export { DetailHandler, EditFormHandler, initWithResp } from './handlers'
export { PaginatedResultObjBase } from './PaginatedResultObjBase'
export { ResultObj, GQLObj, getMappingRes, getMappingMutationRes } from './ResultObj'
export { MemTableObj, getMemTableObj } from './MemTableObj'
export {
  generateEditUI,
  generateDetailUI,
  generateDetailUIByKey,
  getFormFields,
} from './uiBuilders'
export {
  Fields,
  InlineFragment,
  renamed,
  Field,
  extendField,
  Node,
  NodeExt,
  Query,
  Mutation,
  Subscription,
  Edge,
  NodeConnection,
  NodeConnection2,
  QueryItem,
  Var,
  getGQLString,
  generateGQLString,
  generateGQL,
} from './ast'
export { getOrderStrByName, getOrderStrBycCamelName, checkError } from './ngqlUtils'

// ============================================================================
// setUIAdapter wrapper: 어댑터 교체 시 generateDetailUIByKey 자동 주입
// (state.ts 와 uiBuilders.ts 사이의 순환 import 를 피하기 위해 여기서 결합)
// ============================================================================

/**
 * 렌더링에 사용할 UI 프레임워크 어댑터를 교체한다.
 *
 * 기본은 Ant Design 기반 `NgqlUI` 이며, 다른 구현(예: shadcn/ui 바인딩)을 주입하면
 * `GQLObj().getDetailUI()`, `getEditUI()`, `getArrayElem()` 등이 새 어댑터를 통해
 * 렌더링된다. 앱 초기화 시 한 번만 호출하는 것을 권장.
 */
export function setUIAdapter(adapter: NineGQLUIAdapter): void {
  _setUIAdapter(adapter)
  adapter.generateDetailUIByKey = generateDetailUIByKey
}

// 초기 어댑터(NgqlUI) 에 generateDetailUIByKey 주입
;(NgqlUI as NineGQLUIAdapter).generateDetailUIByKey = generateDetailUIByKey

// ============================================================================
// NineGQL default export — 기존 호출처 (`Ngql.GQLObj(...)`) 호환
// ============================================================================

const NineGQL = {
  get UIInfo(): NineGQLUIAdapter {
    return getUIAdapter()
  },
  setUIAdapter,
  setDefaultClient,
  getOrderStrByName,
  getOrderStrBycCamelName,
  Field,
  extendField,
  Node,
  NodeExt,
  renamed,
  InlineFragment,
  Fields,
  Edge,
  NodeConnection,
  NodeConnection2,
  Var,
  QueryItem,
  Query,
  Mutation,
  Subscription,
  GQLObj,
  getMappingRes,
  getMappingMutationRes,
  generateGQLString,
  generateGQL,
  getGQLString,
  generateEditUI,
  generateDetailUI,
  generateDetailUIByKey,
  initWithResp,
  getFormFields,
  getMemTableObj,
  checkError,
  get defaultClient() {
    return ngqlInfo.defaultClient
  },
}

export default NineGQL
